'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import useUser from '@haaretz/s-atoms/user';
import usePpLogout from '@haaretz/s-auth-hooks/use-pp-logout';
import { usePurchasePagePersonalQuery } from '@haaretz/s-pp-queries/PurchasePage';
import generatePath from '@haaretz/s-pp-routing-utils/generatePath';
import getCurrentStep from '@haaretz/s-pp-routing-utils/getCurrentStep';
import getCurrentStepName from '@haaretz/s-pp-routing-utils/getCurrentStepName';
import isGqlPpStep from '@haaretz/s-pp-routing-utils/isGqlPpStep';
import TextLink from '@haaretz/s-text-link';
import useBi from '@haaretz/s-use-bi';
import usePpDataVariables from '@haaretz/s-use-pp-data-variables';
import { QueryObserver, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import * as React from 'react';
import s9 from 'style9';
const c = s9.create({
    base: {
        backgroundColor: "rgb(var(--c-neutral300))",
        textAlign: 'center',
        fontFamily: 'var(--font-brand),"Helvetica Neue",Helvetica,arial,tahoma,sans-serif',
        fontSize: "var(--fz-0)",
        lineHeight: "var(--lh-0)",
        "@media all and (max-width:79.9375em)": {
            marginBottom: "0.5rem"
        },
        "@media all and (min-width:80em) and (max-width:101.9375em)": {
            fontSize: "var(--fz--1)",
            lineHeight: "var(--lh--1)"
        },
        "@media all and (min-width:102em)": {
            fontSize: "var(--fz--2)",
            lineHeight: "var(--lh--2)"
        },
        paddingTop: "0.5rem",
        paddingBottom: "0.5rem"
    },
    specialDataBg: {
        backgroundColor: "rgba(var(--c-neutral200),0.2)"
    },
    offersStepBg: {
        backgroundColor: "rgb(var(--c-primary500))"
    }
});
const pagesNotToShow = [
    'giftSubscription',
    'groupSubscription'
];
export default function ChangeAccountBanner({ inlineStyle, styleExtend = [] }) {
    const router = useRouter();
    const client = useQueryClient();
    const user = useUser('cookieValue');
    const currentStepName = React.useMemo(()=>getCurrentStepName(router.pathname), [
        router.pathname
    ]);
    const biAction = useBi();
    const [shouldRedirect, setShouldRedirect] = React.useState(false);
    const variables = usePpDataVariables();
    const { data: promotionsData } = usePurchasePagePersonalQuery(variables, {
        enabled: false
    });
    const isOffersStep = currentStepName === 'offers';
    const isSpecialOffers = !!promotionsData?.PurchasePage?.pageData?.specialSaleData && isOffersStep;
    const logoutData = usePpLogout({
        onSuccess: (data)=>{
            if (data.success) {
                setShouldRedirect(true);
            }
        }
    });
    React.useEffect(()=>{
        const enabled = shouldRedirect && !variables.couponId && !variables.previousSteps && !variables.selectedOfferId;
        const observer = new QueryObserver(client, {
            enabled,
            queryKey: usePurchasePagePersonalQuery.getKey(variables),
            queryFn: usePurchasePagePersonalQuery.fetcher(variables)
        });
        const unsubscribe = observer.subscribe(({ status, data: ppData })=>{
            if (enabled && status === 'success') {
                const steps = ppData.PurchasePage?.pageData.steps || [];
                const nextStep = steps[0];
                const { pathName, asPath } = generatePath({
                    nextStepName: nextStep?.name || 'offers',
                    asPath: router.asPath,
                    currentQueryParams: router.query,
                    ignoreCurrentSearchParams: true
                });
                router.replace(pathName, asPath, {
                    shallow: true
                });
                setShouldRedirect(false);
            }
        });
        return ()=>{
            unsubscribe();
        };
    }, [
        router,
        client,
        variables,
        currentStepName,
        biAction,
        shouldRedirect
    ]);
    function logoutOnClick(evt) {
        evt.preventDefault();
        const getBiData = ()=>{
            const pageData = promotionsData?.PurchasePage?.pageData;
            const currentStep = pageData && isGqlPpStep(currentStepName) && getCurrentStep(currentStepName, pageData.allSteps).currentStepIndex + 1;
            const additionalInfoBiData = {
                additional_info: {
                    ...currentStep ? {
                        stage_number: currentStep.toString()
                    } : {}
                }
            };
            const biData = {
                stage: currentStepName,
                ...additionalInfoBiData
            };
            return biData;
        };
        biAction({
            ...getBiData()
        });
        logoutData.mutate({});
    }
    if (!user.firstName || pagesNotToShow.includes(currentStepName)) {
        return null;
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: s9(c.base, isSpecialOffers && c.specialDataBg, isOffersStep && c.offersStepBg, ...styleExtend),
        style: inlineStyle,
        children: [
            'שלום',
            " ",
            user.firstName,
            ",",
            ' ',
            /*#__PURE__*/ _jsx(TextLink, {
                href: "/offers",
                onClick: logoutOnClick,
                children: 'להחלפת משתמש'
            })
        ]
    });
}
const colorCssVariablesHtz = [
    "neutral300",
    "neutral200",
    "primary500"
];
const lineHeightCssVariablesHtz = [
    "--lh-0",
    "--lh--1",
    "--lh--2"
];
const fontSizeCssVariablesHtz = [
    "--fz-0",
    "--fz--1",
    "--fz--2"
];

//# sourceMappingURL=ChangeAccountBanner.js.map